'use client';

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, AlertCircle, CheckCircle2, Flag } from 'lucide-react';
import Image from 'next/image';
import useSWR from 'swr';

// Utility function for conditional class names
const cn = (...classes: (string | boolean | undefined)[]) => {
  return classes.filter(Boolean).join(' ');
};

// Simple Avatar component
function Avatar({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-10 w-10 rounded-full overflow-hidden">
      {children}
    </div>
  );
}

interface ReviewFormData {
  title: string;
  content: string;
  rating: number;
}

interface Review {
  id: string;
  title: string;
  content: string;
  rating: number;
  created_at: string;
  profiles: {
    name: string;
    avatar_url?: string;
  };
  reported?: boolean;
}

function StarRating({ 
  value, 
  onChange, 
  readOnly = false,
  size = 'default'
}: { 
  value: number; 
  onChange?: (value: number) => void; 
  readOnly?: boolean;
  size?: 'small' | 'default' | 'large';
}) {
  const stars = Array.from({ length: 5 }, (_, i) => i + 1);
  
  const sizeClasses = {
    small: 'text-base',
    default: 'text-xl',
    large: 'text-2xl'
  };
  
  return (
    <div className="flex items-center" aria-label={`${value} out of 5 stars`} role="img">
      {stars.map((star) => (
        <button
          key={star}
          type="button"
          disabled={readOnly}
          onClick={() => !readOnly && onChange?.(star)}
          className={cn(
            sizeClasses[size],
            'transition-all duration-200 flex items-center justify-center',
            !readOnly && 'hover:scale-110',
            !readOnly ? 'cursor-pointer' : 'cursor-default',
            star <= value 
              ? 'text-accent' 
              : 'text-bg-secondary'
          )}
          aria-label={readOnly ? `${star} stars` : `Rate ${star} stars`}
        >
          <Star className={cn(
            'fill-current',
            star <= value ? 'stroke-accent' : 'stroke-bg-secondary'
          )} />
        </button>
      ))}
    </div>
  );
}

function Toast({ 
  title, 
  description, 
  variant = 'default',
  onClose
}: { 
  title: string;
  description: string;
  variant?: 'default' | 'destructive';
  onClose: () => void;
}) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={cn(
        'fixed top-20 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm',
        'backdrop-blur-md border',
        variant === 'destructive' 
          ? 'bg-status-error/80 border-status-error/70 text-static-white' 
          : 'bg-status-success/80 border-status-success/70 text-static-white'
      )}
    >
      <div className="flex gap-3">
        <div className="mt-0.5">
          {variant === 'destructive' 
            ? <AlertCircle className="h-5 w-5 text-static-white/90" /> 
            : <CheckCircle2 className="h-5 w-5 text-static-white/90" />
          }
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-sm">{title}</h4>
          <p className="text-xs mt-1 opacity-90">{description}</p>
        </div>
        <button 
          onClick={onClose} 
          className="text-static-white/70 hover:text-static-white transition-colors"
          aria-label="Close notification"
        >
          &times;
        </button>
      </div>
    </motion.div>
  );
}

export function ReviewSection({ productId, initialReviews = [] }: { 
  productId: string; 
  initialReviews?: Review[];
}) {
  const t = useTranslations('reviews');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({ title: '', description: '', variant: 'default' as 'default' | 'destructive' });
  const [reportingId, setReportingId] = useState<string | null>(null);
  const [redirectPath, setRedirectPath] = useState('');

  // Use SWR for user data
  const { data: userData } = useSWR('/api/auth/me', async (url) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch user');
    return res.json();
  }, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 60000 // Cache for 1 minute
  });

  const user = userData?.user;

  // Use SWR for reviews data with initial data
  const { data: reviewsData, mutate: mutateReviews } = useSWR<Review[]>(
    `/api/reviews?productId=${productId}`,
    async (url) => {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch reviews');
      const data = await res.json();
      return data.reviews;
    },
    {
      fallbackData: initialReviews,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 30000, // Cache for 30 seconds
      refreshInterval: 60000, // Refresh every minute
      shouldRetryOnError: false
    }
  );

  const reviews = reviewsData || initialReviews;
  
  // Toast implementation
  function toast({ title, description, variant = 'default' }: { title: string; description: string; variant?: 'default' | 'destructive' }) {
    setToastMessage({ title, description, variant });
    setShowToast(true);
    setTimeout(() => setShowToast(false), 5000);
  }
  
  // Get the current path for the login redirect
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setRedirectPath(window.location.pathname + '#reviews');
    }
  }, []);
  
  const { register, handleSubmit, reset, setValue, watch } = useForm<ReviewFormData>({
    defaultValues: {
      title: '',
      content: '',
      rating: 5
    }
  });
  
  const currentRating = watch('rating');
  
  const onSubmit = async (data: ReviewFormData) => {
    if (!user) {
      toast({
        title: t('pleaseSignIn'),
        description: t('signInRequired'),
        variant: 'destructive'
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/reviews', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productId,
          ...data
        })
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to submit review');
      }
      
      if (responseData.success) {
        if (responseData.review?.approved && responseData.review.id !== 'pending') {
          // Update the reviews cache with the new review
          await mutateReviews((prev: Review[] = []) => [responseData.review, ...prev], false);
        }
        
        toast({
          title: t('reviewSubmitted'),
          description: responseData.message || t('reviewSubmittedDescription'),
        });
        
        reset();
      } else {
        throw new Error(responseData.error || 'Review submission failed');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      
      let errorMessage = t('submitError');
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast({
        title: t('errorTitle'),
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleReportReview = async (reviewId: string) => {
    setReportingId(reviewId);
    
    try {
      const response = await fetch(`/api/reviews/${reviewId}/report`, {
        method: 'POST'
      });
      
      if (response.ok) {
        toast({
          title: t('reviewReported'),
          description: t('reviewReportedDescription'),
        });
        
        // Update local state to show the review has been reported
        await mutateReviews((prev: Review[] = []) => 
          prev.map(review => review.id === reviewId ? { ...review, reported: true } : review)
        , false);
      } else {
        throw new Error('Failed to report review');
      }
    } catch (err) {
      console.error('Error reporting review:', err);
      toast({
        title: t('errorTitle'),
        description: t('reportError'),
        variant: 'destructive'
      });
    } finally {
      setReportingId(null);
    }
  };

  // Get first letter of name for avatar fallback
  const getInitials = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  return (
    <section className="space-y-8 py-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight text-text-main">{t('title')}</h2>
        {reviews.length > 0 && (
          <div className="flex items-center gap-2">
            <StarRating 
              value={reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length} 
              readOnly 
            />
            <span className="inline-flex items-center text-sm text-text-main">
              ({reviews.length})
            </span>
          </div>
        )}
      </div>
      
      {/* Toast notification */}
      <AnimatePresence>
        {showToast && (
          <Toast
            title={toastMessage.title}
            description={toastMessage.description}
            variant={toastMessage.variant}
            onClose={() => setShowToast(false)}
          />
        )}
      </AnimatePresence>
      
      {/* Display existing reviews */}
      <div className="space-y-6">
        {reviews.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2">
            {reviews.map(review => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative overflow-hidden rounded-xl bg-surface p-6 transition-all duration-200 hover:shadow-lg"
              >
                <div className="flex justify-between items-center gap-4">
                  <div className="flex gap-3 items-center">
                    <Avatar>
                      {review.profiles.avatar_url ? (
                        <Image src={review.profiles.avatar_url} alt={review.profiles.name} width={40} height={40} />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center rounded-full bg-surface-hover text-text-main">
                          {getInitials(review.profiles.name)}
                        </div>
                      )}
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm text-text-main">{review.profiles.name}</p>
                      <p className="text-xs text-text-main">
                        {formatDate(review.created_at)}
                      </p>
                    </div>
                  </div>
                  <StarRating value={review.rating} readOnly size="small" />
                </div>
                
                <div className="mt-4">
                  <h3 className="font-semibold text-text-main">{review.title}</h3>
                  <p className="mt-2 text-text-main text-sm leading-relaxed">
                    {review.content}
                  </p>
                </div>
                
                {!review.reported ? (
                  <button
                    onClick={() => handleReportReview(review.id)}
                    disabled={reportingId === review.id}
                    className="mt-4 flex items-center gap-1 text-xs text-text-main hover:text-status-error transition-colors duration-200"
                  >
                    <Flag className="h-3 w-3" />
                    <span>{reportingId === review.id ? t('reporting') : t('reportReview')}</span>
                  </button>
                ) : (
                  <div className="mt-4 flex items-center gap-1 text-xs text-text-main italic">
                    <Flag className="h-3 w-3" />
                    <span>{t('reported')}</span>
                  </div>
                )}
                
                {/* Decorative corner element for depth */}
                <div className="absolute -top-6 -right-6 w-12 h-12 bg-gradient-to-br from-primary/10 to-transparent rounded-full opacity-70"></div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 rounded-lg bg-surface">
            <p className="text-text-main">{t('noReviews')}</p>
          </div>
        )}
      </div>
      
      {/* Review form */}
      {user ? (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-12 rounded-xl bg-surface p-6"
        >
          <h3 className="text-xl font-semibold mb-6 text-text-main">{t('writeReview')}</h3>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-text-main">{t('rating')}</label>
              <StarRating 
                value={currentRating} 
                onChange={(value) => setValue('rating', value)}
                size="large"
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="title" className="block text-sm font-medium text-text-main">{t('reviewTitle')}</label>
              <input 
                id="title" 
                className="w-full px-4 py-3 rounded-lg bg-surface-hover text-text-main shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200"
                placeholder={t('titlePlaceholder')}
                {...register('title', { required: true })} 
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="content" className="block text-sm font-medium text-text-main">{t('reviewContent')}</label>
              <textarea 
                id="content" 
                rows={4}
                className="w-full px-4 py-3 rounded-lg bg-surface-hover text-text-main shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none transition-all duration-200" 
                placeholder={t('contentPlaceholder')}
                {...register('content', { required: true })} 
              />
            </div>
            
            <button 
              type="submit" 
              disabled={isSubmitting}
              className="px-6 py-3 w-full sm:w-auto bg-primary hover:bg-primary/90 text-static-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:pointer-events-none"
            >
              {isSubmitting ? t('submitting') : t('submit')}
            </button>
          </form>
          
          {/* Decorative gradient for depth */}
          <div className="absolute -left-4 -bottom-4 w-64 h-64 bg-gradient-to-tr from-primary/5 to-transparent rounded-full blur-3xl -z-10"></div>
        </motion.div>
      ) : (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-12 rounded-xl bg-surface p-8 text-center"
        >
          <p className="text-text-main">
            {t('signInToReview')}{' '}
            <Link 
              href={{
                pathname: '/login',
                query: { redirectTo: redirectPath } 
              }}
              className="text-primary font-medium hover:underline transition-all"
            >
              {t('signIn')}
            </Link>
          </p>
        </motion.div>
      )}
    </section>
  );
} 